<template>
  <div class="iv-filters">
    <!-- Timeframe presets — these change the SERVER date window (refetch). -->
    <div class="iv-tf-row">
      <button
        v-for="tf in timeframes"
        :key="tf.key"
        type="button"
        class="iv-tf-btn"
        :class="{ 'iv-tf-btn--active': activeTimeframe === tf.key }"
        @click="$emit('change-timeframe', tf.key)"
      >
        {{ tf.label }}
      </button>

      <!--
        Appointment status. Also a SERVER control (refetch), not a row filter — completed
        appointments were never fetched, so there is nothing client-side to filter.
        Default is Scheduled: this page is the day's worklist. "Completed" exists because a
        patient dropped off it the moment their visit was marked complete, which left no way
        to verify them at all.
      -->
      <div class="iv-aptstatus" role="group" aria-label="Appointment status">
        <button
          type="button"
          class="iv-tf-btn iv-aptstatus-btn"
          :class="{ 'iv-tf-btn--active': !includeCompleted }"
          @click="$emit('toggle-completed', false)"
        >Scheduled</button>
        <button
          type="button"
          class="iv-tf-btn iv-aptstatus-btn"
          :class="{ 'iv-tf-btn--active': includeCompleted }"
          title="Also list patients whose appointment has already been completed"
          @click="$emit('toggle-completed', true)"
        >+ Completed</button>
      </div>
    </div>

    <div class="iv-filter-controls">
      <!-- Patient search (client-side) -->
      <div class="iv-search-wrap">
        <i class="fa fa-search iv-search-icon" aria-hidden="true"></i>
        <!--
          Typing filters the loaded rows (instant). ENTER searches EVERY patient — the only
          way to reach someone with no appointment in the window, which is the case the
          client-side filter can never answer.
        -->
        <input
          class="iv-input iv-search-input"
          type="text"
          :value="search"
          placeholder="Search patient name… (Enter to search all)"
          @input="$emit('change-search', $event.target.value)"
          @keydown.enter.prevent="$emit('search-all')"
        />
      </div>

      <!-- Carrier filter (client-side substring match) -->
      <input
        class="iv-input iv-carrier-input"
        type="text"
        :value="carrier"
        placeholder="Filter by insurance carrier…"
        @input="$emit('change-carrier', $event.target.value)"
      />

      <!-- Native date range — inline-label style (per RecallFilterCard). These
           change the SERVER date window (refetch). -->
      <div class="iv-date-range">
        <i class="fa fa-calendar iv-date-icon" aria-hidden="true"></i>
        <input
          class="iv-input iv-date-input"
          type="date"
          :value="from"
          :max="to || undefined"
          @input="$emit('change-dates', { from: $event.target.value, to: to })"
        />
        <span class="iv-date-sep">to</span>
        <input
          class="iv-input iv-date-input"
          type="date"
          :value="to"
          :min="from || undefined"
          @input="$emit('change-dates', { from: from, to: $event.target.value })"
        />
      </div>

      <span v-if="showClear" class="iv-clear" @click="$emit('clear-all')">
        <i class="fa fa-times" aria-hidden="true"></i> Clear filters
      </span>
    </div>

    <!-- Active issue-chip filter pill -->
    <div v-if="issueLabel" class="iv-issue-pill">
      Showing: {{ issueLabel }}
      <span class="iv-issue-x" @click="$emit('clear-issue')">
        <i class="fa fa-times" aria-hidden="true"></i>
      </span>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { TIMEFRAMES } from './constants.js';

export default {
  name: 'VerificationFilters',
  props: {
    // Active timeframe preset key, or null when a custom date range is in play.
    activeTimeframe: { type: String, default: null },
    from: { type: String, default: '' },      // YMD
    to: { type: String, default: '' },        // YMD
    search: { type: String, default: '' },
    carrier: { type: String, default: '' },
    // Human label of the active Action-Required issue filter ('' when none).
    issueLabel: { type: String, default: '' },
    // Whether completed appointments are included. A SERVER concern, not a row filter.
    includeCompleted: { type: Boolean, default: false },
  },
  data() {
    return {
      timeframes: TIMEFRAMES,
    };
  },
  computed: {
    showClear() {
      return !!(this.search || this.carrier || this.issueLabel);
    },
  },
};
</script>

<style lang="scss" scoped>
.iv-filters {
  padding: 16px 18px;
  border-bottom: 1px solid #dfe4e3;
}

.iv-tf-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

// Pushed to the far end and given a divider so it doesn't read as a sixth timeframe
// preset — it answers a different question ("which appointments", not "when").
.iv-aptstatus {
  display: inline-flex;
  gap: 8px;
  margin-left: auto;
  padding-left: 12px;
  border-left: 1px solid #e2e8f0;
}

.iv-tf-btn {
  padding: 7px 13px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #dfe4e3;
  background: #ffffff;
  color: #545b59;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    border-color: #c8cfcd;
  }
  &--active {
    background: #0b5c49;
    border-color: #0b5c49;
    color: #ffffff;
  }
}

.iv-filter-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.iv-input {
  height: 40px;
  border: 1px solid #c8cfcd;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  background: #ffffff;
  color: #0c0f0e;
  font-family: inherit;
  padding: 0 12px;
  box-sizing: border-box;

  &:focus {
    border-color: #2fa683;
    box-shadow: 0 0 0 2px rgba(47, 166, 131, 0.15);
  }
}

.iv-search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 300px;
}

.iv-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca4a2;
  font-size: 14px;
  pointer-events: none;
}

.iv-search-input {
  width: 100%;
  padding-left: 34px;
}

.iv-carrier-input {
  min-width: 210px;
}

.iv-date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #717977;
}

.iv-date-icon {
  color: #9ca4a2;
  font-size: 14px;
}

.iv-date-input {
  height: 40px;
  padding: 0 10px;
  font-size: 13px;
}

.iv-date-sep {
  color: #717977;
}

.iv-clear {
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: #0b5c49;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.iv-issue-pill {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 6px 12px;
  background: #eceff0;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 600;
  color: #262b2a;
}

.iv-issue-x {
  cursor: pointer;
  color: #717977;
  display: flex;

  &:hover {
    color: #262b2a;
  }
}
</style>
